/**
 * Unified resolution of sales-agent and customer-service display names
 * for request list/index tables and filter result mappers.
 *
 * API fields (RequestForCardResource / RequestResource):
 * - agent: agent_name, user.name, userBeforeSendingToAdministration.name
 * - CS:    customer_service_name, cs_name, customer_service.name
 *          (when customer_services_status === 'cs_side', CS user is in customer_service)
 */

const CS_BASKET_STATUSES = ["admin_side", "cs_side"];

/**
 * Unwrap nested list rows (quality_request.request, items.request, etc.).
 * @param {object} record
 * @returns {object|null}
 */
export function unwrapRequestRecord(record) {
  if (!record) return null;
  if (record.quality_request?.request) return record.quality_request.request;
  if (record.request && typeof record.request === "object") return record.request;
  return record;
}

/**
 * Sales agent (استشاري) display name for a request list row.
 * @param {object} record
 * @param {{ hideAgentOnCsBasket?: boolean, fallback?: string|null }} [options]
 * @returns {string|null}
 */
export function getRequestAgentName(record, options = {}) {
  const req = unwrapRequestRecord(record);
  if (!req) return options.fallback ?? null;

  const hideOnCsBasket = options.hideAgentOnCsBasket !== false;
  if (
    hideOnCsBasket &&
    CS_BASKET_STATUSES.includes(req.customer_services_status)
  ) {
    return options.fallback ?? null;
  }

  const name =
    req.agent_name ??
    req.user?.name ??
    req.userBeforeSendingToAdministration?.name ??
    null;

  return name ?? options.fallback ?? null;
}

/**
 * Customer service (خدمة العملاء) display name for a request list row.
 * @param {object} record
 * @param {{ fallback?: string|null }} [options]
 * @returns {string|null}
 */
export function getRequestCustomerServiceName(record, options = {}) {
  const req = unwrapRequestRecord(record);
  if (!req) return options.fallback ?? null;

  const name =
    req.customer_service_name ??
    req.cs_name ??
    req.customer_service?.name ??
    null;

  return name ?? options.fallback ?? null;
}

/**
 * Common table-row keys used by admin index pages and filter dialogs.
 * Spread into setTblData objects: { ...buildRequestAgentCsTableFields(res) }
 *
 * @param {object} record
 * @param {{ hideAgentOnCsBasket?: boolean, fallback?: string|null, includeArabicKeys?: boolean }} [options]
 * @returns {{ agent_name: string|null, customer_service_name: string|null, "استشاري المبيعات"?: string|null }}
 */
export function buildRequestAgentCsTableFields(record, options = {}) {
  const agentName = getRequestAgentName(record, options);
  const csName = getRequestCustomerServiceName(record, options);

  const fields = {
    agent_name: agentName,
    customer_service_name: csName,
  };

  if (options.includeArabicKeys !== false) {
    fields["استشاري المبيعات"] = agentName;
  }

  return fields;
}
